import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import validator from "validator";
import { forgotPassword, validateOTP, resetPassword } from "../../services/AuthServices";
import Utils from "../../utils";
import useSeo from "../../utils/useSeo";
import "../../asset/style/auth.css";

const STEP = { EMAIL: 1, OTP: 2, RESET: 3, DONE: 4 };

const ForgotPassword = () => {
  useSeo({
    title: "Recuperar Palavra-passe",
    description:
      "Recupere o acesso à sua conta TP Software. Enviamos um código por email para repor a sua palavra-passe em segurança.",
    path: "/forgot-password",
    noindex: true,
  });

  const [step, setStep] = useState(STEP.EMAIL);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetToken, setResetToken] = useState("");

  const submitEmail = (e) => {
    e.preventDefault();
    setError("");
    if (!email) return setError("Email é obrigatório");
    if (!validator.isEmail(email)) return setError("Email inválido");

    setLoading(true);
    forgotPassword({ email: email.trim() })
      .then(() => {
        toast.success("Código enviado para o seu email.");
        setStep(STEP.OTP);
      })
      .catch((err) => toast.error(Utils.getErrorMessage(err)))
      .finally(() => setLoading(false));
  };

  const submitOtp = (e) => {
    e.preventDefault();
    setError("");
    if (!otp) return setError("Introduza o código");
    setLoading(true);
    validateOTP({ email: email.trim(), otp: otp.trim() })
      .then((r) => {
        const tok = r?.data?.token || r?.data?.data?.token || "";
        if (!tok) {
          toast.error("Não foi possível obter o token de recuperação.");
          return;
        }
        setResetToken(tok);
        setStep(STEP.RESET);
      })
      .catch((err) => toast.error(Utils.getErrorMessage(err)))
      .finally(() => setLoading(false));
  };

  const submitReset = (e) => {
    e.preventDefault();
    setError("");
    if (!password) return setError("Palavra-passe é obrigatória");
    if (password.length < 6) return setError("Mínimo 6 caracteres");
    if (password !== confirmPassword) return setError("Palavras-passe não coincidem");

    setLoading(true);
    resetPassword({
      email: email.trim(),
      password,
      confirm_password: confirmPassword,
      token: resetToken,
    })
      .then(() => {
        toast.success("Palavra-passe atualizada!");
        setStep(STEP.DONE);
      })
      .catch((err) => toast.error(Utils.getErrorMessage(err)))
      .finally(() => setLoading(false));
  };

  return (
    <div className="tp-auth">
      <div className="tp-auth-left">
        <div className="tp-auth-left-content">
          <img
            src="https://tp.software/libs/images/tpsoftware-green-white.png"
            alt="TP Software"
            className="tp-auth-logo"
          />
          <h1>Recuperar acesso.</h1>
          <p>
            Vamos enviar-lhe um código para repor a sua palavra-passe em poucos
            passos.
          </p>
        </div>
      </div>

      <div className="tp-auth-right">
        <div className="tp-auth-card">
          <div className="tp-auth-steps">
            {[1, 2, 3].map((s) => (
              <div
                key={s}
                className={`tp-auth-step${step >= s ? " active" : ""}${step === s ? " current" : ""}`}
              >
                {s}
              </div>
            ))}
          </div>

          {step === STEP.EMAIL && (
            <>
              <h2>Esqueceu a palavra-passe?</h2>
              <p className="tp-auth-sub">
                Indique o seu email e enviaremos um código de recuperação.
              </p>
              <form onSubmit={submitEmail}>
                <div className="tp-auth-field">
                  <label>Email</label>
                  <input
                    type="email"
                    className={`tp-auth-input${error ? " error" : ""}`}
                    placeholder="exemplo@empresa.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  {error && <span className="tp-auth-error">{error}</span>}
                </div>
                <button type="submit" className="tp-auth-submit" disabled={loading}>
                  {loading ? "A enviar..." : "Enviar código"}
                </button>
              </form>
            </>
          )}

          {step === STEP.OTP && (
            <>
              <h2>Verificar código</h2>
              <p className="tp-auth-sub">
                Enviámos um código de 6 dígitos para <strong>{email}</strong>.
              </p>
              <form onSubmit={submitOtp}>
                <div className="tp-auth-field">
                  <label>Código</label>
                  <input
                    type="text"
                    className={`tp-auth-input${error ? " error" : ""}`}
                    placeholder="000000"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    maxLength={6}
                    style={{ letterSpacing: "8px", textAlign: "center", fontSize: 20 }}
                  />
                  {error && <span className="tp-auth-error">{error}</span>}
                </div>
                <button type="submit" className="tp-auth-submit" disabled={loading}>
                  {loading ? "A verificar..." : "Validar código"}
                </button>
                <button
                  type="button"
                  className="tp-auth-link-btn"
                  onClick={() => setStep(STEP.EMAIL)}
                >
                  ← Voltar
                </button>
              </form>
            </>
          )}

          {step === STEP.RESET && (
            <>
              <h2>Nova palavra-passe</h2>
              <p className="tp-auth-sub">Escolha uma palavra-passe segura.</p>
              <form onSubmit={submitReset}>
                <div className="tp-auth-field">
                  <label>Nova palavra-passe</label>
                  <input
                    type="password"
                    className="tp-auth-input"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
                <div className="tp-auth-field">
                  <label>Confirmar palavra-passe</label>
                  <input
                    type="password"
                    className={`tp-auth-input${error ? " error" : ""}`}
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                  {error && <span className="tp-auth-error">{error}</span>}
                </div>
                <button type="submit" className="tp-auth-submit" disabled={loading}>
                  {loading ? "A atualizar..." : "Atualizar palavra-passe"}
                </button>
              </form>
            </>
          )}

          {step === STEP.DONE && (
            <>
              <div className="tp-auth-success">
                <div className="tp-auth-success-icon">✓</div>
                <h2>Tudo pronto!</h2>
                <p className="tp-auth-sub">
                  A sua palavra-passe foi atualizada com sucesso. Pode iniciar
                  sessão.
                </p>
                <Link to="/login" className="tp-auth-submit" style={{ display: "block", textAlign: "center", textDecoration: "none" }}>
                  Entrar na conta
                </Link>
              </div>
            </>
          )}

          <div className="tp-auth-footer">
            <Link to="/login">← Voltar ao login</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
